import { useState } from "react";
import { Aviso, Botao, Cartao } from "../ui";
import { mensagemDoConsole } from "../../nucleo/console";
import {
  ROTULO_DA_CATEGORIA,
  ROTULO_DO_STATUS,
  useAtivos,
  useAtivosDoChamado,
  useDefinirAtivosDoChamado,
} from "../../nucleo/ativos";
import { podeNaSessao, useSessao } from "../../nucleo/sessao";

/**
 * Os ativos de um chamado. Quem tem `ativo:ver` lê; quem tem `ativo:gerenciar` liga e desliga.
 * Só os ativos da organização do chamado são oferecidos — o servidor confere de novo.
 */
export function AtivosDoChamado({
  ticketId,
  organizacaoId,
}: {
  ticketId: string;
  organizacaoId: string;
}) {
  const sessao = useSessao();
  const pode = podeNaSessao(sessao.data, "ativo:ver");
  const podeGerenciar = podeNaSessao(sessao.data, "ativo:gerenciar");
  const vinculados = useAtivosDoChamado(ticketId, pode);
  const [editando, setEditando] = useState(false);
  const [escolhidos, setEscolhidos] = useState<string[]>([]);
  const disponiveis = useAtivos({ organizacaoId }, podeGerenciar && editando);
  const definir = useDefinirAtivosDoChamado(ticketId);

  if (!pode) return null;
  // Quem só lê não ganha um cartão vazio em todo chamado: sem ativo ligado, nada a mostrar.
  if (!podeGerenciar && vinculados.data && vinculados.data.length === 0) return null;

  function abrir() {
    setEscolhidos((vinculados.data ?? []).map((ativo) => ativo.id));
    setEditando(true);
  }

  return (
    <Cartao titulo="Ativos" data-testid="ativos-do-chamado" className="mb-4">
      {vinculados.isPending ? <p role="status">Carregando ativos…</p> : null}
      {vinculados.isError ? <Aviso tom="erro">{mensagemDoConsole(vinculados.error)}</Aviso> : null}
      {vinculados.data && vinculados.data.length === 0 && !editando ? (
        <p>Nenhum ativo ligado a este chamado.</p>
      ) : null}
      {!editando ? (
        <ul>
          {vinculados.data?.map((ativo) => (
            <li key={ativo.id}>
              <b>{ativo.nome}</b>{" "}
              <span className="celula-secundaria">
                {ROTULO_DA_CATEGORIA[ativo.categoria]}
                {ativo.numero_serie ? ` · série ${ativo.numero_serie}` : ""} · {ROTULO_DO_STATUS[ativo.status]}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <div data-testid="escolha-de-ativos">
          {disponiveis.isPending ? <p role="status">Carregando os ativos da empresa…</p> : null}
          {disponiveis.data && disponiveis.data.length === 0 ? (
            <p>Esta empresa ainda não tem ativos cadastrados.</p>
          ) : null}
          {disponiveis.data?.map((ativo) => (
            <label key={ativo.id} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={escolhidos.includes(ativo.id)}
                onChange={(evento) =>
                  setEscolhidos((atual) =>
                    evento.target.checked ? [...atual, ativo.id] : atual.filter((id) => id !== ativo.id),
                  )
                }
              />
              <span>
                {ativo.nome}{" "}
                <span className="celula-secundaria">
                  {ROTULO_DA_CATEGORIA[ativo.categoria]}
                  {ativo.numero_serie ? ` · série ${ativo.numero_serie}` : ""}
                </span>
              </span>
            </label>
          ))}
          {definir.isError ? <Aviso tom="erro" className="mt-2">{mensagemDoConsole(definir.error)}</Aviso> : null}
          <div className="flex gap-2 mt-2">
            <Botao
              tamanho="pequeno"
              disabled={definir.isPending}
              onClick={() => definir.mutate(escolhidos, { onSuccess: () => setEditando(false) })}
              data-testid="salvar-ativos-do-chamado"
            >
              {definir.isPending ? "Salvando…" : "Salvar"}
            </Botao>
            <Botao tamanho="pequeno" variante="fantasma" onClick={() => setEditando(false)}>
              Cancelar
            </Botao>
          </div>
        </div>
      )}
      {podeGerenciar && !editando ? (
        <Botao className="mt-2" tamanho="pequeno" variante="fantasma" onClick={abrir} data-testid="ligar-ativos">
          Ligar ativos
        </Botao>
      ) : null}
    </Cartao>
  );
}
